import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import CourseIcon from '../components/CourseIcon';
import { useAuth } from '../context/AuthContext';
import { createPaymentOrder, verifyPayment } from '../services/payments';
import { listCourses } from '../services/courses';
function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (typeof window !== 'undefined' && window.Razorpay) { resolve(); return; }
    const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload  = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Razorpay'));
    document.body.appendChild(s);
  });
}

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const AUDIENCE_TABS = [
  { id: 'professional', label: 'For Professionals' },
  { id: 'college',      label: 'For Colleges' },
];

const LEVEL_TABS = [
  { id: 'all',          label: 'All Courses' },
  { id: 'beginner',     label: 'Beginner' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'advanced',     label: 'Advanced' },
];

const Courses = () => {
  const { user, token, loading: authLoading, isAuthenticated, refreshUser } = useAuth();
  const [courses,        setCourses]        = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [coursesError,   setCoursesError]   = useState('');
  const [payingCourseId, setPayingCourseId] = useState(null);
  const [audience,       setAudience]       = useState('professional');
  const [tab,            setTab]            = useState('all');

  useEffect(() => {
    let cancelled = false;
    setCoursesLoading(true);
    listCourses()
      .then((res) => { if (!cancelled) setCourses(res.data?.data || []); })
      .catch(() => { if (!cancelled) setCoursesError('Could not load courses. Please refresh.'); })
      .finally(() => { if (!cancelled) setCoursesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const subscribedIds  = new Set((user?.subscribed_courses || []).map((s) => s.course_id));
  const isPortalAdmin  = user?.role === 'admin';
  const showSubscribeUi = isAuthenticated && !isPortalAdmin && !authLoading;

  const professionalCourses = courses.filter((c) => c.category !== 'college');
  const collegeCourses      = courses.filter((c) => c.category === 'college');

  const filtered = audience === 'college'
    ? collegeCourses
    : tab === 'all'
      ? professionalCourses
      : professionalCourses.filter((c) => c.category === tab);

  const handleSubscribe = useCallback(async (course) => {
    if (!token || !course.course_id) return;
    setPayingCourseId(course.course_id);
    try {
      await loadRazorpayScript();
      const res = await createPaymentOrder(token, course.course_id);
      if (!res.data?.success || !res.data?.data) throw new Error(res.data?.message || 'Could not create order');
      const d = res.data.data;
      const options = {
        key:         d.keyId,
        amount:      String(d.amount),
        currency:    d.currency,
        name:        d.name || 'InvFly',
        description: d.description || course.title,
        order_id:    d.orderId,
        prefill:     d.prefill || {},
        theme:       { color: '#00D4AA' },
        handler: async (response) => {
          try {
            await verifyPayment(token, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_signature:  response.razorpay_signature,
            });
            await refreshUser();
          } catch (err) {
            window.alert(err.response?.data?.message || err.message || 'Could not verify payment');
          }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => { window.alert(resp.error?.description || 'Payment failed'); });
      rzp.open();
    } catch (err) {
      window.alert(err.response?.data?.message || err.message || 'Could not start checkout');
    } finally {
      setPayingCourseId(null);
    }
  }, [token, refreshUser]);

  return (
    <>
      <Seo
        title="Courses — InvFly"
        description="Structured stock market courses for beginners, investors, traders, and college students. Indian market focused, concept-first education."
      />

      <div className="ph">
        <div className="container">
          <div className="ph-tag">Curriculum</div>
          <div className="ph-title">Our Courses</div>
          <p className="ph-desc">Structured programs from foundation to advanced — concept-first, discipline-driven, Indian market focused.</p>
        </div>
      </div>

      <section className="sec">
        <div className="container">

          {/* Audience selector */}
          <div className="audience-tabs">
            {AUDIENCE_TABS.map((a) => (
              <button
                key={a.id}
                className={`audience-tab${audience === a.id ? ' active' : ''}`}
                onClick={() => { setAudience(a.id); setTab('all'); }}
              >
                {a.label}
              </button>
            ))}
          </div>

          {/* Level sub-tabs — only for professionals */}
          {audience === 'professional' && (
            <div className="tabs" style={{ marginTop: '20px' }}>
              {LEVEL_TABS.map((t) => (
                <button key={t.id} className={`tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {coursesLoading && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-2)', fontSize: 15 }}>
              Loading courses…
            </div>
          )}

          {!coursesLoading && coursesError && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--danger, #f87171)', fontSize: 15 }}>
              {coursesError}
            </div>
          )}

          {!coursesLoading && !coursesError && filtered.length === 0 && audience === 'professional' && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-2)', fontSize: 15 }}>
              No courses found in this category.
            </div>
          )}

          {!coursesLoading && !coursesError && filtered.length > 0 && audience === 'professional' && (
            <div className="course-grid">
              {filtered.map((c, i) => {
                const isSubscribed = subscribedIds.has(c.course_id);
                const isPaying     = payingCourseId === c.course_id;
                return (
                  <div key={c.course_id} className="cc" style={{ animationDelay: `${i * 60}ms`, cursor: 'default' }}>
                    <div className="cc-header-row">
                      <CourseIcon icon={c.icon} />
                      <div className="cc-title">{c.title}</div>
                    </div>
                    <div className="cc-for">For: {c.target_audience}</div>
                    <ul className="cc-list">
                      {(c.learn_highlights || []).map((l, j) => <li key={j}>{l}</li>)}
                    </ul>
                    <div style={{ fontSize: '12px', color: 'var(--text-2)', padding: '10px 0', borderBottom: '1px solid var(--border)', fontStyle: 'italic' }}>
                      Outcome: {c.outcome_summary}
                    </div>
                    <div className="cc-footer">
                      <div className="cc-price"><small>₹</small>{c.price}</div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Link to={`/courses/${c.course_id}`} className="btn-enr-s">Details</Link>
                        {showSubscribeUi && isSubscribed && (
                          <span style={{ fontSize: '12px', color: 'var(--accent)', fontFamily: 'var(--fm)' }}>✓ Enrolled</span>
                        )}
                        {showSubscribeUi && !isSubscribed && c.is_enrollable && (
                          <button className="btn-enr" disabled={isPaying} onClick={() => handleSubscribe(c)}>
                            {isPaying ? '…' : 'Enroll'}
                          </button>
                        )}
                        {showSubscribeUi && !isSubscribed && !c.is_enrollable && (
                          <Link to="/contact" className="btn-enr-s">Enquire</Link>
                        )}
                        {!authLoading && !isAuthenticated && (
                          <Link to="/signin" className="btn-enr">Sign In</Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {audience === 'college' && !coursesLoading && (
            <div style={{ marginTop: '32px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '32px' }}>
              <div className="s-tag" style={{ marginBottom: '10px' }}>Industry Education Partner</div>
              <div style={{ fontFamily: 'var(--fd)', fontSize: '22px', fontWeight: '700', color: 'var(--text)', marginBottom: '12px', letterSpacing: '-.02em' }}>College & University Programs</div>
              <p style={{ fontSize: '14px', color: 'var(--text-2)', lineHeight: '1.8', maxWidth: '600px', marginBottom: '24px' }}>InvFly partners with colleges and universities to deliver NEP 2020 aligned, credit-based financial markets education. Programs range from 1-day orientation seminars to full 30–60 hour certification courses.</p>
              <Link to="/contact" className="btn-p">Partner with Us <ArrowIcon /></Link>
            </div>
          )}
        </div>
      </section>

      <div className="cta-sec">
        <div className="container">
          <div className="cta-title">Sharpen Your Edge with <span className="txt-ac">Free Tools</span></div>
          <p className="cta-desc">Pair your course learning with our calculators — plan your SIPs, size your trades, and manage risk with discipline.</p>
          <div className="cta-acts">
            <Link to="/tools" className="btn-p">Explore Tools <ArrowIcon /></Link>
            <Link to="/contact" className="btn-s">Contact Us</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Courses;
